/**
 * Contact interface for arcano.app
 * This code is under copyright Arcano Gmbh (https://arcano.app).
 *
 * - no commercial use
 * - no modifications
 * - no redistribution
 *
 * without written consent of Arcano Gmbh, zentralstrasse 47, 8003 zürich, switzerland, https://arcano.app
*/

'use strict';

import {createElemWithClasses, delay, mfetch} from "./utils";
// import {contactWidget} from "./contact-widget";
import {gModal} from "./gmodal";
import {translate} from "./translations";
import {setupContactClicks} from "./contact-widget";
import {isUserView} from "./upload";
import {sidebarToggleOnMobile} from "./v2";


const LIMIT = 5;


function createContact(db: IDBDatabase, e: MouseEvent) {
    const btn = e.currentTarget as HTMLElement;
    const li = btn.closest('li')
    if(!li) {
        return
    }
    const url = new URL(li.dataset.arcanoLink!);
    const uid = url.searchParams.get('public');
    if(!uid) {
        return
    }

    mfetch('/create_contact', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            uid: uid,
        })
    }).then((r) => {
        if (r.status === 200){
            btn.remove()
            const ok = createElemWithClasses('img', []) as HTMLImageElement;
            ok.src = "/img/icons/outline/check-green.svg"
            ok.height = 16;
            li.append(ok)
        }
    }).finally(() => {
        setupContactsTable(db, '')
    })
}


function deleteContactPrompt(uid: string) {

    function _deleteContact() {
        mfetch('/delete_contact', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                uid: uid,
            })
        }).finally(() => {
            window.location.reload()
        })
    }

    new gModal({
        title: translate('Delete contact'),
        body: translate('If you remove a contact, the files received from this person will be shown as Anonymous'),
        buttons: [
            {
                content: translate("Delete"),
                classes: "btn btn-danger ms-auto bootbox-accept",
                bindKey: 'Enter',
                callback: _deleteContact
            }
        ],
        close: {
            closable: true
        }
    }).show();
}

export function deleteContact() {

    const contact = isUserView()
    if(!contact) {
        return
    }
    const url = new URL(contact.dataset.arcanoLink!);
    const uid = url.searchParams.get('public');
    if(!uid) {
        return
    }
    deleteContactPrompt(uid)
}



export function showAddContactPrompt(db: IDBDatabase) {
    let title = translate('Add contact')

    let message = `
        <div class="mb-3">
            ${translate('You can only add users that have enabled to be found')}.
        </div>
        <div id="add-contact-search" class="d-flex">
            <input type="search" id="add-contact-search-field" class="form-control me-2" placeholder="Search contacts">
            <img src="/img/icons/outline/search.svg" alt="Search">
        </div>
        <ul id="add-contact-search-result"></ul>        
        `;


    const modal = new gModal({
        title: title,
        body: message,
        close: {
            closable: true
        },
        onShow: () => {
            const emailInput = createElemWithClasses('input') as HTMLInputElement
            emailInput.type = 'email'
            const search = document.getElementById('add-contact-search-field') as HTMLInputElement
            const contactList = document.getElementById('add-contact-search-result')
            if(!search || !contactList) {return}
            search.addEventListener('keyup', delay(() => {

                emailInput.value = search.value  // validate via e-mail type input
                if(!emailInput.validity.valid) {
                    return
                }

                mfetch(`/users/list/?limit=${LIMIT}&searchTerm=${search.value}&search=1`).then((r)=>r.json()).then((data: Data) => {
                    contactList.innerText = '';
                    for (let i=0; i< data.contacts.length; i++) {
                        const contactLi = createElemWithClasses('li', [])
                        contactList.appendChild(contactLi);
                        const img = createElemWithClasses('img') as HTMLImageElement;
                        contactLi.appendChild(img)
                        img.src = '/img/icons/outline/user-1.svg'
                        const inner = createElemWithClasses('span')
                        inner.innerText = data.contacts[i].fullname + ' (' + data.contacts[i].email + ')';

                        contactLi.appendChild(inner)

                        if(data.contacts[i].existing_contact) {
                            const button = createElemWithClasses('img', ['create-contact-button']) as HTMLImageElement
                            button.src = '/img/icons/outline/check-green.svg'
                            contactLi.appendChild(button)
                        } else {
                            const button = createElemWithClasses('button', ['btn', 'btn-sm', 'create-contact-button'])
                            contactLi.appendChild(button)
                            button.innerText = translate('Add')
                            button.classList.add('btn-info')
                            button.addEventListener('click', (e) => createContact(db, e))
                        }

                        contactLi.dataset.arcanoLink = data.contacts[i].arcano_link
                        contactLi.dataset.email = data.contacts[i].email
                        contactLi.dataset.uid = data.contacts[i].uid

                    }
                }).then(() => {}).catch(() => {})
            }, 300))
        }
    });
    modal.show();
    return modal;
}


interface Contact {
    subscription: boolean;
    uid: string;
    existing_contact: boolean;
    blocked: string,
    contact_id: number,
    files: string,
    group: string,
    arcano_link: string,
    // username: string.

    email: string,
    fullname: string,
    phone: string


}
interface Group {
    name: string;
    uid: string,
}
interface Data {
    contacts: Array<Contact>,
    groups: Array<Group>

}
/**
 * Set up the contacts table
 */
function setupContactsTable(db: IDBDatabase, searchTerm: string) {

    const contactList = document.getElementById('contact-list') as HTMLUListElement;
    if(!contactList) {
        return
    }
    contactList.innerText = '';
    mfetch(`/contacts/list/?limit=${LIMIT}&searchTerm=${searchTerm}` ).then((r)=>r.json()).then((data: Data) => {

        if(data.contacts.length === 0) {

            const contactLi = createElemWithClasses('li', ['sidebar-button', 'text-wrap'])
            contactList.appendChild(contactLi);

            contactLi.innerHTML = `
                ${translate("No contacts found")}.
                <br>
                Click here to search for Arcano users
            `;  // note: save innerHtml
            contactLi.addEventListener('click', () => showAddContactPrompt(db))
        }

        for (let i=0; i< data.contacts.length; i++) {
            const contactLi = createElemWithClasses('li', ['sidebar-button', 'arcano-uploader-widget', 'open-uppy-button'])
            contactList.appendChild(contactLi);
            const img = createElemWithClasses('img') as HTMLImageElement;
            contactLi.appendChild(img)
            img.src = '/img/icons/outline/user-1.svg'
            const inner = createElemWithClasses('span')
            inner.innerText = data.contacts[i].fullname + ' (' + data.contacts[i].email + ')';

            contactLi.appendChild(inner)

            contactLi.dataset.arcanoLink = data.contacts[i].arcano_link;
            contactLi.dataset.email = data.contacts[i].email;
            contactLi.dataset.uid = data.contacts[i].uid
            if(data.contacts[i].subscription) {
                contactLi.dataset.subscription = 'true';
            }
            contactLi.addEventListener('click', sidebarToggleOnMobile)
        }
    }).then(() => { setupContactClicks(db) }).catch(() => {})
}


export function setupContacts(db: IDBDatabase) {

    const searchField = document.getElementById('contact-search-field') as HTMLInputElement;
    if(searchField) {
        searchField.addEventListener('keyup',  delay(() => {
            setupContactsTable(db, searchField.value)
        }, 300))
    }

    const addContactButton = document.getElementById('sidebar-add-contact-button');
    if(addContactButton) {
        addContactButton.addEventListener('click', () => showAddContactPrompt(db))
    }

    setupContactsTable(db, '');
}



